import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

import { set, z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AuthLogin, AuthRegister, FetchMe } from "./ApiRoutes"
import { useContext, useState } from "react"
import { toast, Toaster } from "sonner"
import { Link, useNavigate } from "react-router-dom"
import { ArrowLeft, ArrowRight, Check, CircleAlert, Dot } from "lucide-react"
import { UserContext } from "@/App"
import LoginDialog from "./LoginDialog"
import { SecondaryLoader } from "@/utils/Loaders"


const formSchema = z.object({
    email: z.string().email({
        message: "Invalid email address"
    }),
    confirmEmail: z.string().email({
        message: "Invalid email address"
    }),
    username: z.string().min(3, {
        message: "Username must be at least 3 characters long"
    }).max(50, {
        message: "Username must be at most 50 characters long"
    }).refine(value => !/\s/.test(value), {
        message: "Username cannot contain spaces",
    }),
    password: z.string().min(8,{
        message:"Password must be at least 8 characters long"
    }).refine((password) => !/^\d+$/.test(password), {
      message: "Password can't be entirely numeric.",
    }),
    confirmPassword: z.string().min(8,{
        message:"Password must be at least 8 characters long"
    }),
}).superRefine(({ email, confirmEmail, password, confirmPassword }, ctx) => {
    if (email !== confirmEmail) {
        ctx.addIssue({
            code: 'custom',
            message: "Emails don't match",
            path: ['confirmEmail'],
        });
    }
    if (password !== confirmPassword) {
        ctx.addIssue({
            code: 'custom',
            message: "Passwords don't match",
            path: ['confirmPassword'],
        });
    }
});




interface LoginDialogProps {
    children: React.ReactNode;
}

export default function RegisterDialog({children}: LoginDialogProps) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const {user,setUser} = useContext(UserContext);
    let isAuth = false;

    // form is too big for one dialog, so we need to split it between 2 steps
    const [step, setStep] = useState<1|2|3>(1); 

    // switches to step 2 if form is valid
    const handleNext = async () => {
        const isStepOneValid = await form.trigger(["email", "confirmEmail"]);
        if (isStepOneValid) {
            setStep(2); 
        }
    };

    const handleSubmit = (values: z.infer<typeof formSchema>) => {
        postRegister.mutate({ email: values.email, username: values.username, password: values.password });
    }

    const {isPending: isPending, isError, data: profileData, isSuccess, error } = useQuery({
        queryKey: ['profile', localStorage.getItem('username')],
        queryFn: () => FetchMe(),
        enabled: !!isAuth,
    });
  
    if (isSuccess && profileData) {
        setUser(profileData);
    }

  const postLogin = useMutation({
        mutationFn: (variables) => 
            AuthLogin(variables.username, variables.password),

        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['login', variables.username] });
            localStorage.setItem('accessToken', data.access);
            localStorage.setItem('refreshToken', data.refresh);
            localStorage.setItem('username', variables.username);
            isAuth = true;
            toast.success("Log in successful");
            setTimeout(() => {
                navigate('/profile/' + variables.username + '/');
                setIsOpen(false);
                window.location.reload(); // reload to update the user context
            }, 500);
        },
        onError: (error) => {
            toast.error("Failed to login.");
        }
    });


    const postRegister = useMutation({
        mutationFn: (variables) => 
            AuthRegister(variables.email,variables.username, variables.password),

        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['register', variables.username] });
            toast.success("Account has been created");
            setStep(3);

            setTimeout(() => {
                 postLogin.mutate({ username:variables.username , password:variables.password  })
            }, 500);
            
        },
        onError: (error) => {
            toast.error("Failed to register.");
        }
    });

    
    const form = useForm<z.infer<typeof formSchema>>({
        // The resolver now uses the single, unified schema.
        resolver: zodResolver(formSchema),
        mode: "onBlur",
        defaultValues: {
            email: "",
            confirmEmail: "",
            username: "",
            password: "",
            confirmPassword: "",
        }
    });

    

    return (
        <>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
        
        <Form {...form}>
            <DialogTrigger asChild>
            {children}
            </DialogTrigger>
            <DialogContent className="flex bg-black/40 backdrop-blur-md items-center justify-center dark text-white">
                <form onSubmit={form.handleSubmit(handleSubmit)} className="">
            <DialogHeader className="flex flex-col items-center justify-center">
                <div className="flex justify-center items-center gap-2">
                    { step === 1 ? 
    
                    <p className="font-semibold text-xl">1</p>
                    :
                    <p className="text-neutral-400">1</p>
                    }
                    
                    
                    { step === 2 ? 
                        <p className="font-semibold text-xl">2</p>
                        :
                        <p className="text-neutral-400">2</p>
                    }
                </div>
                <DialogTitle className="font-semibold text-xl mb-0">{step === 3 ? "Thanks for joining!" : "One of us. One of us."}</DialogTitle>
                <DialogDescription>{step == 3 ? "Logging you in :)" : "Create an account" }</DialogDescription>
            </DialogHeader>
            <section className="flex flex-col justify-center items-center my-4">
            
            {step === 1 && (<>
                <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                <FormItem className="mb-4 w-55">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                    <Input placeholder="abediscool@community.edu" {...field} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
                )}
                />
                <FormField
                    control={form.control}
                    name="confirmEmail"
                    render={({ field }) => (
                    <FormItem className="mb-4 w-55">
                        <FormLabel>Confirm email</FormLabel>
                        <FormControl>
                        <Input placeholder="abediscool@community.edu" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                    )}
                />
            </>
            )}
            {step === 2 && (
            <>
                <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                <FormItem className="mb-4 w-55">
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                    <Input placeholder="cyrilsucks13" {...field} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                <FormItem className="mb-4 w-55">
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                    <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                <FormItem className="mb-4 w-55">
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                    <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
                )}
            />
            
            </>
            )}
            
            
            { step === 1 &&
            <Button className="flex w-60 hover:cursor-pointer" type="button" onClick={handleNext}>Next <ArrowRight /></Button>
            }
            { step === 2 &&
             <div className="flex w-full gap-2 items-center justify-center">
                <Button className="flex hover:cursor-pointer" onClick={() => setStep(1)} type="button"  variant={"outline"}>
                    <ArrowLeft />Back
                </Button>
                <Button className="flex hover:cursor-pointer" type="submit">
                    {isAuth ? <Check /> : "Register"}
                </Button>
            </div>
            }
           
           { step === 3 &&
            <div className="flex flex-col mb-2 gap-2 justify-center items-center text-sm max-w-80 text-green-500">
                <DialogClose asChild>
                    <>
                        <LoginDialog>
                            <SecondaryLoader />
                        </LoginDialog>
                    </>
                </DialogClose>
                
            </div>
            }
            </section>

        
           
            { step !== 3 &&
                    <aside className="flex flex-col justify-center items-center text-sm">
                    <p className="text-neutral-500">
                        Already with us?{' '}
                        <LoginDialog>
                            <span className="text-white hover:cursor-pointer hover:underline">
                                Log in
                            </span>
                        </LoginDialog>
                    </p>

                </aside>
            }
            
            </form>
            </DialogContent>
        </Form>
        </Dialog>
    </>
    )
}

